export interface WeekSpec { week: number; year: number }

export function isoWeeksInYear(year: number): number {
  // ISO 8601 §4.3.2: a year has 53 weeks iff Jan 1 or Dec 31 is a Thursday.
  const jan1Dow = new Date(Date.UTC(year, 0, 1)).getUTCDay() || 7
  const dec31Dow = new Date(Date.UTC(year, 11, 31)).getUTCDay() || 7
  return jan1Dow === 4 || dec31Dow === 4 ? 53 : 52
}

export function parseWeekString(s: string): WeekSpec {
  const m = /^(\d{1,2})-(\d{4})$/.exec(s.trim())
  if (!m) throw new Error(`Ongeldig weekformaat: "${s}". Verwacht "wk-jaar", bijv. "13-2026".`)
  const week = parseInt(m[1], 10)
  const year = parseInt(m[2], 10)
  if (week < 1 || week > 53) throw new Error(`Week moet tussen 1 en 53 liggen (kreeg ${week}).`)
  const maxWeek = isoWeeksInYear(year)
  if (week > maxWeek) {
    throw new Error(`Jaar ${year} heeft maar ${maxWeek} ISO-weken (kreeg week ${week}).`)
  }
  return { week, year }
}

export function mondayOfIsoWeek(week: number, year: number): Date {
  // ISO 8601: week 1 bevat de eerste donderdag van het jaar.
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const jan4Dow = jan4.getUTCDay() || 7 // 1=Mon..7=Sun
  const mondayOfWeek1 = new Date(jan4)
  mondayOfWeek1.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1))
  const result = new Date(mondayOfWeek1)
  result.setUTCDate(mondayOfWeek1.getUTCDate() + (week - 1) * 7)
  return result
}

export function weekStringFromDate(d: Date): string {
  const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  const dow = target.getUTCDay() || 7
  target.setUTCDate(target.getUTCDate() + 4 - dow) // naar donderdag van die week
  const year = target.getUTCFullYear()
  const jan1 = new Date(Date.UTC(year, 0, 1))
  const week = Math.ceil(((target.getTime() - jan1.getTime()) / 86400000 + 1) / 7)
  return `${week}-${year}`
}

export function weeksBetween(start: WeekSpec, end: WeekSpec): number {
  const ms = mondayOfIsoWeek(end.week, end.year).getTime() - mondayOfIsoWeek(start.week, start.year).getTime()
  return Math.round(ms / (7 * 86400000))
}

/**
 * ISO-week-string van een datum, mét zondagnacht-correctie voor per-week
 * nacht-roosters.
 *
 * ISO-8601 stopt een ZONDAG in de week die ÓP die zondag eindigt (de week die
 * de vorige maandag begon). Voor een zondagnacht-rooster (de nachtdienst start
 * op zondagnacht en leidt de werkweek in) hoort die zondag juist bij de week
 * die hij INLEIDT — de maandag erná. Zonder deze correctie vertaalt de
 * werkbon-create een datumbereik dat op zondag(nacht) begint naar de ISO-week
 * vóór de bedoelde werkweek, waardoor de werkbonnen een week te vroeg (en in
 * dubbel) gegenereerd worden.
 *
 * Dit spiegelt de `zondagLeidtWerkweekIn`-logica in `generateRecurringDates`
 * (waar de leidende zondag op `monday - 1` valt): de week-mapping en de
 * dag-generatie moeten het over dezelfde week-toewijzing eens zijn.
 *
 * @param zondagLeidtWerkweekIn  true wanneer een per-week nacht-rooster actief
 *   is (== de modal-flag `zondagnachtAlsStart` / de server-`zondagnachtFlag`).
 */
export function weekStringForShiftDate(d: Date, zondagLeidtWerkweekIn: boolean): string {
  // getUTCDay(): 0 = zondag. Alleen een zondag schuift naar de volgende week.
  if (zondagLeidtWerkweekIn && d.getUTCDay() === 0) {
    const next = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1))
    return weekStringFromDate(next)
  }
  return weekStringFromDate(d)
}

export interface IsoWeekGroup {
  weekKey: string
  weekMonday: Date
  dates: Date[]
}

/**
 * Groepeert losse data per ISO-week. Voor elke ingangs-datum bepalen we de
 * maandag-van-die-ISO-week en aggregeren we onder dezelfde key.
 *
 * Gebruikt door POST /werkbonnen losse-dagen/datumbereik-paden om N werkbonnen
 * aan te maken — één per week. De `weekKey` is `weekStringFromDate(d)` (formaat
 * `"<wk>-<jaar>"`); `weekMonday` is de maandag in UTC zodat we direct in
 * `werkbon.startDatum` kunnen schrijven.
 *
 * Gegarandeerd: groepen zijn gesorteerd op `weekMonday` oplopend, en `dates`
 * binnen een groep idem.
 */
export function groupDatesByIsoWeek(dates: Date[]): IsoWeekGroup[] {
  if (dates.length === 0) return []
  const byKey = new Map<string, IsoWeekGroup>()
  for (const d of dates) {
    const key = weekStringFromDate(d)
    const existing = byKey.get(key)
    if (existing) {
      existing.dates.push(d)
    } else {
      const { week, year } = parseWeekString(key)
      byKey.set(key, {
        weekKey: key,
        weekMonday: mondayOfIsoWeek(week, year),
        dates: [d],
      })
    }
  }
  const groups = Array.from(byKey.values())
  for (const g of groups) g.dates.sort((a, b) => a.getTime() - b.getTime())
  groups.sort((a, b) => a.weekMonday.getTime() - b.weekMonday.getTime())
  return groups
}
